import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * AddActionModal Component
 * Modal for creating new actions with form validation
 */
const AddActionModal = ({ isOpen, onClose, onSubmit, useCaseTitle }) => {
  const [formData, setFormData] = useState({
    label: '',
    description: '',
    role: '',
    priority: 'Medium',
    due_window: '30 days'
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setFormData({
        label: '',
        description: '',
        role: '',
        priority: 'Medium',
        due_window: '30 days'
      });
      setErrors({});
    }
  }, [isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.label.trim()) {
      newErrors.label = 'Action label is required';
    }

    if (!formData.role.trim()) {
      newErrors.role = 'Responsible role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      // Form will be reset by useEffect when modal closes
    } catch (error) {
      console.error('Failed to create action:', error);
      setErrors({ submit: 'Failed to create action. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-labelledby="modal-title"
          aria-modal="true"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 id="modal-title" className="text-xl font-bold text-gray-900">
                Add New Action
              </h2>
              {useCaseTitle && (
                <p className="text-sm text-gray-600 mt-1">
                  For: {useCaseTitle}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close modal"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Label Field */}
            <div>
              <label htmlFor="action-label" className="block text-sm font-medium text-gray-700 mb-2">
                Action Label <span className="text-red-500">*</span>
              </label>
              <input
                id="action-label"
                type="text"
                value={formData.label}
                onChange={(e) => handleChange('label', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors ${
                  errors.label ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., Schedule kickoff meeting with IT team"
                disabled={isSubmitting}
              />
              {errors.label && (
                <p className="mt-1 text-sm text-red-600">{errors.label}</p>
              )}
            </div>

            {/* Description Field */}
            <div>
              <label htmlFor="action-description" className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                id="action-description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors resize-none"
                placeholder="Provide additional context or details about this action..."
                disabled={isSubmitting}
              />
            </div>

            {/* Role Field */}
            <div>
              <label htmlFor="action-role" className="block text-sm font-medium text-gray-700 mb-2">
                Responsible Role <span className="text-red-500">*</span>
              </label>
              <select
                id="action-role"
                value={formData.role}
                onChange={(e) => handleChange('role', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors ${
                  errors.role ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isSubmitting}
              >
                <option value="">Select a role...</option>
                <option value="Director of Informatics">Director of Informatics</option>
                <option value="IT Leadership">IT Leadership</option>
                <option value="Clinical Leadership">Clinical Leadership</option>
                <option value="Data Analytics Team">Data Analytics Team</option>
                <option value="Project Manager">Project Manager</option>
                <option value="Implementation Team">Implementation Team</option>
                <option value="Vendor">Vendor</option>
                <option value="Compliance Officer">Compliance Officer</option>
                <option value="CFO">CFO</option>
                <option value="CMIO">CMIO</option>
                <option value="CIO">CIO</option>
              </select>
              {errors.role && (
                <p className="mt-1 text-sm text-red-600">{errors.role}</p>
              )}
            </div>

            {/* Priority and Due Window - Side by Side */}
            <div className="grid grid-cols-2 gap-4">
              {/* Priority Field */}
              <div>
                <label htmlFor="action-priority" className="block text-sm font-medium text-gray-700 mb-2">
                  Priority
                </label>
                <select
                  id="action-priority"
                  value={formData.priority}
                  onChange={(e) => handleChange('priority', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors"
                  disabled={isSubmitting}
                >
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>

              {/* Due Window Field */}
              <div>
                <label htmlFor="action-due" className="block text-sm font-medium text-gray-700 mb-2">
                  Due Window
                </label>
                <select
                  id="action-due"
                  value={formData.due_window}
                  onChange={(e) => handleChange('due_window', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors"
                  disabled={isSubmitting}
                >
                  <option value="7 days">7 days</option>
                  <option value="14 days">14 days</option>
                  <option value="30 days">30 days</option>
                  <option value="60 days">60 days</option>
                  <option value="90 days">90 days</option>
                  <option value="6 months">6 months</option>
                  <option value="12 months">12 months</option>
                </select>
              </div>
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{errors.submit}</p>
              </div>
            )}

            {/* Footer Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                onClick={onClose}
                variant="outline"
                disabled={isSubmitting}
                className="px-5 py-2"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Creating...
                  </>
                ) : (
                  'Add Action'
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddActionModal;